import React, { useState } from 'react';
import {
  Typography, Paper, Button, TextField, Grid, Box, IconButton, Snackbar, Alert, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

const StepTwo = ({ selectedElements, url, onCopyMapping, pubDateIncomplete, contentType }) => {
  // State for the form fields
  const [spiderName, setSpiderName] = useState('archer-labs-ncdor-us-nc-dor-press-releases');
  const [apiTable, setApiTable] = useState('legal_api_docs');
  const [metaTable, setMetaTable] = useState('agency_updates');
  const [jsonGenerated, setJsonGenerated] = useState(false);
  const [jsonConfig, setJsonConfig] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const apiTableOptions = [
    'fed_api_docs',
    'scrapinghub_api_docs', 
    'gua_api_docs',
    'legal_api_docs',
    'crawlbot_api_docs',
    'arbitrary_api_docs',
    'reg_gov_api_docs',
    'uk_gov_api_docs',
    'open_states_api_docs'
  ];

  const metaTableOptions = [
    'reginfo',
    'enforcements',
    'agency_updates',
    'rules',
    'bill_dockets',
    'document_version_docket',
    'predidential_documents',
    'whitepapers',
    'mainstream_news',
    'all'
  ];

  // Generate JSON config
  const handleGenerateJson = () => {
    // Map selectedElements to config structure
    const articlesPath = selectedElements['content'];
    const itemPath = selectedElements['url'];
    const paginationPath = selectedElements['pagination'];
    const detailsPath = selectedElements['details'] || selectedElements['content']; // fallback to content for details
    const titlePath = selectedElements['title'];
    const summaryPath = selectedElements['summary'];
    const pubDatePath = selectedElements['pubdate'];
    const contextPath = selectedElements['context'];
    const effectiveDatePath = selectedElements['effective_date'];
    const categoryPath = selectedElements['category'];
    // Build item_step.doc dynamically
    const itemDoc = {};
    if (titlePath) {
      itemDoc.title = { path: titlePath };
      // Add regex if it exists
      if (selectedElements['title_regex']) {
        itemDoc.title.regex = selectedElements['title_regex'];
      }
    }
    if (itemPath) {
      itemDoc.web_url = { path: itemPath };
      // Add regex if it exists
      if (selectedElements['url_regex']) {
        itemDoc.web_url.regex = selectedElements['url_regex'];
      }
    }
    if (summaryPath) {
      itemDoc.summary_text = { path: summaryPath };
      // Add regex if it exists
      if (selectedElements['summary_regex']) {
        itemDoc.summary_text.regex = selectedElements['summary_regex'];
      }
    }
    if (pubDatePath) {
      itemDoc.pub_date = { path: pubDatePath };
      // Add regex if it exists
      if (selectedElements['pubdate_regex']) {
        itemDoc.pub_date.regex = selectedElements['pubdate_regex'];
      }
    }
    if (effectiveDatePath) {
      itemDoc.effective_date = { path: effectiveDatePath };
      // Add regex if it exists
      if (selectedElements['effective_date_regex']) {
        itemDoc.effective_date.regex = selectedElements['effective_date_regex'];
      }
    }
    if (categoryPath) {
      itemDoc.scraped_category = { 
        path: categoryPath,
        is_text: true
      };
      // Add regex if it exists
      if (selectedElements['category_regex']) {
        itemDoc.scraped_category.regex = selectedElements['category_regex'];
      }
      itemDoc.category = "conversion";
    }
    if (pubDateIncomplete) itemDoc.incomplete_pub_date = true;

    // Build details_step.doc if detailsPath is present
    const detailsDoc = {};
    if (detailsPath) {
      if (contentType === 'PDF') {
        detailsDoc.pdf_url = { path: detailsPath };
      } else {
        detailsDoc.html = { path: detailsPath };
      }
    }

    // Steps
    const steps = {
      description: 'Initializing steps, that should be performed by the current crawler.',
      start: {
        description: 'Starting step that loads main web-page.',
        next_step: [
          articlesPath ? 'articles_step' : null,
          paginationPath ? '>> next_page' : null
        ].filter(Boolean)
      }
    };

    if (paginationPath) {
      steps['>> next_page'] = {
        description: 'get the next page',
        context: {
          path: paginationPath,
          is_url: true
        },
        next_step: articlesPath ? 'articles_step' : undefined
      };
    }

    if (articlesPath) {
      steps['articles_step'] = {
        description: 'Getting list of articles for the current page.',
        context: {
          path: contextPath
        },
        next_step: itemPath ? 'item_step' : undefined
      };
    }

    if (itemPath) {
      steps['item_step'] = {
        description: 'Getting metadata of the current article.',
        doc: Object.keys(itemDoc).length > 0 ? itemDoc : undefined,
        context: {
          path: itemPath,
          is_url: true
        },
        next_step: detailsPath ? 'details_step' : undefined
      };
    }

    if (detailsPath) {
      steps['details_step'] = {
        description: 'getting full_text of the doc.',
        save_document: true,
        doc: Object.keys(detailsDoc).length > 0 ? detailsDoc : undefined
      };
    }

    // Remove undefined keys from steps
    Object.keys(steps).forEach(key => {
      if (typeof steps[key] === 'object' && steps[key] !== null) {
        Object.keys(steps[key]).forEach(subKey => {
          if (steps[key][subKey] === undefined) {
            delete steps[key][subKey];
          }
        });
      }
    });

    const config = {
      spider_name: spiderName,
      api_table: apiTable,
      meta_table: metaTable,
      configurations: {
        ...(categoryPath && {
          category_conversion: [
            {
              conversion_map: selectedElements['category_conversion_map'] || {
                "Suspension": "Suspension Order",
                "Revocation": "Revocation Order"
              },
              default_category: selectedElements['category_conversion_map'] && Object.values(selectedElements['category_conversion_map'])[0] || "Enforcement Order"
            }
          ]
        }),
        steps
      }
    };
    setJsonConfig(JSON.stringify(config, null, 2));
    setJsonGenerated(true);
  };

  // Copy JSON to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(jsonConfig);
    if (onCopyMapping) onCopyMapping(jsonConfig);
    setSnackbarOpen(true);
  };

  return (
    <Paper 
      elevation={0}
      sx={{ 
        mb: 2, 
        p: { xs: 2, md: 3 }, 
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04)'
      }}
    >
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>Crawler Configuration</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          label="Spider Name"
          value={spiderName}
          onChange={e => setSpiderName(e.target.value)}
          fullWidth
          size="medium"
          variant="outlined"
          sx={{ minWidth: 400, flex: 1 }}
        />
        <FormControl fullWidth size="medium" sx={{ minWidth: 400, flex: 1 }}>
          <InputLabel>API Table</InputLabel>
          <Select
            value={apiTable}
            onChange={e => setApiTable(e.target.value)}
            label="API Table"
          >
            {apiTableOptions.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="medium" sx={{ minWidth: 400, flex: 1 }}>
          <InputLabel>Meta Table</InputLabel>
          <Select
            value={metaTable}
            onChange={e => setMetaTable(e.target.value)}
            label="Meta Table"
          >
            {metaTableOptions.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Button
        variant="contained"
        sx={{ mb: 3, fontWeight: 600, textTransform: 'none', bgcolor: '#1677ff', '&:hover': { bgcolor: '#156de0' } }}
        onClick={handleGenerateJson}
      >
        Generate JSON
      </Button>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        Generated Configuration JSON
      </Typography>
      <Box
        sx={{
          position: 'relative',
          bgcolor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: 2,
          p: 0,
          fontFamily: 'monospace',
          fontSize: '0.95rem',
          overflow: 'visible',
          p: 2,
          mb: 2
        }}
      >
        {jsonGenerated ? (
          <SyntaxHighlighter
            language="json"
            style={oneLight}
            customStyle={{ margin: 0, background: 'transparent', fontSize: '1rem', padding: 16, minHeight: 120 }}
          >
            {jsonConfig}
          </SyntaxHighlighter>
        ) : (
          <Box sx={{ p: 2, color: '#888' }}>
            // Click "Generate JSON" to see the configuration.
          </Box>
        )}
        <IconButton
          aria-label="Copy JSON"
          onClick={handleCopy}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            boxShadow: 1,
            '&:hover': { bgcolor: '#f5f7fa' }
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          JSON copied to clipboard!
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default StepTwo; 